import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Save, Check } from "lucide-react";
import { useHaptics } from "@/hooks/use-haptics";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";

export const fmt = (n: number, decimals = 2) =>
  isNaN(n) || !isFinite(n) ? "—" : n.toFixed(decimals).replace(/\.?0+$/, "") || "0";

export const ResultRow = ({ label, value, unit }: { label: string; value: string; unit: string }) => (
  <div className="trade-card flex justify-between items-center">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="font-mono-data text-lg text-primary font-bold">
      {value} <span className="text-xs text-muted-foreground font-normal">{unit}</span>
    </span>
  </div>
);

export const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">
    {children}
  </label>
);

export const SaveButton = ({ onSave }: { onSave: (note: string) => void }) => {
  const [saved, setSaved] = useState(false);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const { success } = useHaptics();

  const handleConfirm = () => {
    onSave(note.trim());
    setOpen(false);
    setNote("");
    setSaved(true);
    success();
    toast.success("Saved to Job Log");
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        size="sm"
        className={`w-full mt-3 border-primary/30 ${saved ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-primary hover:border-primary"}`}
      >
        {saved ? <Check className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        {saved ? "Saved!" : "Save to Job Log"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Save to Job Log</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <FieldLabel>Job Name / Note (optional)</FieldLabel>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Unit 4B kitchen circuit"
                className="bg-secondary text-foreground border-border"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <DialogClose asChild>
              <Button variant="ghost" size="sm">Cancel</Button>
            </DialogClose>
            <Button onClick={handleConfirm} size="sm" className="bg-primary text-primary-foreground">
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
